import { create } from 'zustand';
import apiClient from '../utils/apiClient';

// Evaluation envelopes + entries (chat/log). Store shape:
//   all        — every envelope, sorted modified DESC (top-level /evaluations page).
//                 One row per (report, variant) — a flagged run shows up once per
//                 known BioInterpreter variant (see the backend's KNOWN_VARIANTS).
//   byReport   — per-(report, variant) cache: { [`${reportId}:${variant||'default'}`]: { envelope, entries, lastFetched } }
//                Powered by /evaluation-request action="by_report", which returns
//                both envelope and entries in one round trip for one variant.
// A single mutation invalidates both caches so views stay consistent.

const cacheKey = (reportId, variant) => `${reportId}:${variant || 'default'}`;

export const useEvaluationStore = create((set, get) => ({
  all: [],
  allLoading: false,
  allError: null,
  allLastFetched: 0,

  byReport: {},

  cacheDuration: 30 * 1000,

  fetchAll: async (force = false) => {
    const now = Date.now();
    if (!force && now - get().allLastFetched < get().cacheDuration) return get().all;
    set({ allLoading: true, allError: null });
    try {
      const rows = await apiClient.get('/evaluation-list');
      set({
        all: Array.isArray(rows) ? rows : [],
        allLoading: false,
        allLastFetched: now,
      });
      return Array.isArray(rows) ? rows : [];
    } catch (err) {
      set({ all: [], allLoading: false, allError: err.message || 'Failed to load evaluations' });
      return [];
    }
  },

  fetchForReport: async (reportId, variant, force = false) => {
    if (!reportId) return { envelope: null, entries: [] };
    const key = cacheKey(reportId, variant);
    const cached = get().byReport[key];
    const now = Date.now();
    if (!force && cached && now - cached.lastFetched < get().cacheDuration) return cached;
    const res = await apiClient.post('/evaluation-request', {
      action: 'by_report',
      report_id: reportId,
      variant: variant || null,
    });
    const rec = Array.isArray(res?.records) && res.records[0] ? res.records[0] : {};
    const entry = {
      envelope: rec.envelope || null,
      entries: Array.isArray(rec.entries) ? rec.entries : [],
      lastFetched: now,
    };
    set((s) => ({ byReport: { ...s.byReport, [key]: entry } }));
    return entry;
  },

  // Post a BioInterpreter evaluation (kind="bio_interpreter") for one
  // variant's envelope. Each submission appends a fresh entry so the log
  // is auditable — the same reviewer can re-submit and the history is
  // preserved.
  postBioInterpreter: async (reportId, variant, {
    rating,
    dataRecalledAccurately,
    summaryAndConclusions,
    scientificDiscussionAndInterpretation,
    citations,
    comment,
  }) => {
    if (!reportId) return null;
    const saved = await apiClient.post('/evaluation-entry', {
      report_id: reportId,
      variant: variant || null,
      kind: 'bio_interpreter',
      rating,
      data_recalled_accurately: dataRecalledAccurately,
      summary_and_conclusions: summaryAndConclusions,
      scientific_discussion_and_interpretation: scientificDiscussionAndInterpretation,
      citations,
      comment,
    });
    set((s) => ({
      byReport: { ...s.byReport, [cacheKey(reportId, variant)]: undefined },
      allLastFetched: 0,
    }));
    return saved;
  },

  setStatus: async (reportId, variant, status) => {
    if (!reportId || !status) return null;
    const res = await apiClient.post('/evaluation-request', {
      action: 'set_status',
      report_id: reportId,
      variant: variant || null,
      status,
    });
    set((s) => ({
      byReport: { ...s.byReport, [cacheKey(reportId, variant)]: undefined },
      allLastFetched: 0,
    }));
    return Array.isArray(res?.records) && res.records[0] ? res.records[0] : null;
  },

  deleteEntry: async (id, reportId, variant) => {
    await apiClient.delete(`/evaluation-entry/${id}`);
    set((s) => ({
      byReport: { ...s.byReport, [cacheKey(reportId, variant)]: undefined },
      allLastFetched: 0,
    }));
  },
}));
